import { useEffect, useRef } from 'react'

declare global {
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        config: {
          videoId: string
          events?: {
            onReady?: (event: { target: any }) => void
            onStateChange?: (event: { data: number }) => void
          }
        }
      ) => {
        getVideoUrl: () => string
        destroy?: () => void
      }
      PlayerState: {
        PLAYING: number
        PAUSED: number
        ENDED: number
        BUFFERING: number
        CUED: number
      }
    }
    onYouTubeIframeAPIReady?: () => void
  }
}

function extractVideoId(url: string): string | null {
  try {
    const urlObj = new URL(url)
    if (urlObj.hostname.includes('youtu.be')) {
      return urlObj.pathname.slice(1)
    }
    if (urlObj.hostname.includes('youtube.com')) {
      // Para URLs como https://www.youtube.com/embed/VIDEO_ID
      if (urlObj.pathname.startsWith('/embed/')) {
        const parts = urlObj.pathname.split('/')
        const videoId = parts[parts.length - 1]
        return videoId || null
      }
      // Para URLs como https://www.youtube.com/watch?v=VIDEO_ID
      return urlObj.searchParams.get('v')
    }
    return null
  } catch {
    return null
  }
}

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.YT?.Player) {
      resolve()
      return
    }

    const existingScript = document.querySelector(
      'script[src="https://www.youtube.com/iframe_api"]'
    )
    if (existingScript) {
      if (window.YT?.Player) {
        resolve()
        return
      }
      const originalCallback = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        if (originalCallback) originalCallback()
        resolve()
      }
      setTimeout(() => {
        if (!window.YT?.Player) {
          reject(new Error('YouTube API timeout'))
        }
      }, 10000)
      return
    }

    window.onYouTubeIframeAPIReady = () => {
      resolve()
    }

    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    tag.async = true
    tag.onerror = () => {
      reject(new Error('Failed to load YouTube API'))
    }
    const firstScriptTag = document.getElementsByTagName('script')[0]
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag)
    setTimeout(() => {
      if (!window.YT?.Player) {
        reject(new Error('YouTube API timeout'))
      }
    }, 10000)
  })
}

interface UseYouTubePlayerProps {
  containerRef: React.RefObject<HTMLDivElement>
  onVideoPlay?: (src: string) => void
  contentKey?: string
}

export function useYouTubePlayer({ containerRef, onVideoPlay, contentKey }: UseYouTubePlayerProps) {
  const playersRef = useRef<Map<string, unknown>>(new Map())

  useEffect(() => {
    if (!onVideoPlay || !containerRef.current) return

    let cancelled = false

    const initializePlayers = async () => {
      try {
        await loadYouTubeAPI()
      } catch {
        return
      }

      if (cancelled || !window.YT?.Player || !containerRef.current) return

      const videoWrappers = containerRef.current.querySelectorAll<HTMLElement>(
        '[data-video-embed][data-youtube-player-id]'
      )

      if (videoWrappers.length === 0) return

      videoWrappers.forEach((wrapper) => {
        const src = wrapper.getAttribute('data-video-src')
        const playerId = wrapper.getAttribute('data-youtube-player-id')
        if (!src || !playerId) return

        const playerDiv = document.getElementById(playerId)
        if (!playerDiv || playerDiv.querySelector('iframe') || playersRef.current.has(playerId)) {
          return
        }

        const videoId = extractVideoId(src)
        if (!videoId || !window.YT) return

        try {
          const player = new window.YT.Player(playerId, {
            videoId,
            events: {
              onStateChange: (event) => {
                if (window.YT && event.data === window.YT.PlayerState.PLAYING) {
                  onVideoPlay(src)
                }
              },
            },
          })
          playersRef.current.set(playerId, player)
        } catch (error) {
          console.warn('Failed to create YouTube player:', error)
        }
      })
    }

    initializePlayers()

    return () => {
      cancelled = true
    }
  }, [containerRef, onVideoPlay, contentKey])

  useEffect(() => {
    return () => {
      const players = playersRef.current
      players.forEach((player) => {
        try {
          ;(player as { destroy?: () => void }).destroy?.()
        } catch {
          // Ignore destroy errors
        }
      })
      players.clear()
    }
  }, [])
}
